import { Moment } from 'moment';
import _isEmpty from 'lodash/isEmpty';
import _flatMap from 'lodash/flatMap';
import { helpers } from '@minecraft.utils';
import {
  gqlRole,
  gqlProfileSearchInput,
  gqlAgeRangeInput,
  gqlIdRangeInput,
  gqlTalentUnionEnum as TALENT_UNION,
  gqlTalentUnionEnum,
} from '@minecraft.graphql-types';
import { SelectOption } from '@minecraft.typings';
import { sagAftraOption, sagAftraChildrenExclusion } from '../config';
import { ValueOf, FilterData, FilterValues, SelectRadioOption, WillingToEnum, FiltersNameEnum } from '../types';
import { getLocations, removeTimeZoneFromDate } from '../utils';

type CommonArgs = {
  roleDetails: gqlRole;
};

type TalentUnionsArgs = {
  autofillByRoleCriteria?: boolean;
  array: { value: string | number; label: string }[];
};

type GetEthnicAppearanceArgs = CommonArgs;

type GetPlayableAgeRangeArgs = {
  switchToName: FilterData['name'];
  ageMinName: FilterData['name'];
  ageMaxName: FilterData['name'];
} & Omit<CommonArgs, 'name'>;

type GetWorkingLocationArgs = CommonArgs;

// Prepare values when autofill by role createria is checked

const getAge = (ageInYears: boolean, ageMonth: number): string | number => {
  if (!ageMonth) return '';

  if (ageInYears) {
    return helpers.convertMonthToYear(ageMonth);
  }

  return ageMonth;
};

const safeGetValue = (item) => (item?.hasOwnProperty('value') ? item.value : item);

export const getEthnicAppearance = ({ roleDetails }: GetEthnicAppearanceArgs): ValueOf<FilterValues> => {
  if (_isEmpty(roleDetails.ethnicAppearance)) return [];

  return roleDetails?.ethnicAppearance?.map((item) => ({
    value: item.code,
    label: item.name,
  }));
};

export const getPlayableAgeRange = ({
  switchToName,
  ageMinName,
  ageMaxName,
  roleDetails,
}: GetPlayableAgeRangeArgs): FilterValues => {
  return {
    [switchToName]: !roleDetails.ageInYears,
    [ageMinName]: getAge(roleDetails.ageInYears, roleDetails.ageMinMonth),
    [ageMaxName]: getAge(roleDetails.ageInYears, roleDetails.ageMaxMonth),
  };
};

export const getWorkingLocation = ({ roleDetails }: GetWorkingLocationArgs): ValueOf<FilterValues> => {
  if (_isEmpty(roleDetails?.publicationSettings) && _isEmpty(roleDetails?.projectReleaseLocations)) return [];

  const roleWorkingLocations = [
    ..._flatMap(roleDetails?.publicationSettings, (publicationSetting) => publicationSetting.dmas),
    ...(roleDetails?.projectReleaseLocations || []),
  ];

  const {
    usLocations = [],
    caLocations = [],
    euLocations = [],
    auLocations = [],
    additionalLocations = [],
  } = getLocations({
    dmas: roleWorkingLocations,
  });

  const roleWorkingCities = _flatMap(
    [...usLocations, ...caLocations, ...euLocations, ...auLocations, ...additionalLocations],
    (location) => location.cities
  );

  return roleWorkingCities.map((item) => ({
    value: item.id,
    label: item.name,
  }));
};

// Prepare filter input

type GetEntityIdsArgs = {
  asNumber?: boolean;
  array: { value: string | number }[];
};

type RangeArgs = {
  max: string | number | { value: string };
  min: string | number | { value: string };
};

export const getEntityIds = ({ asNumber, array }: GetEntityIdsArgs): (string | number)[] => {
  if (!Array.isArray(array)) {
    return [];
  }

  return array.map((item) => (asNumber ? Number(item.value) : item.value));
};

type FieldNameOption = {
  value: string;
  label: string;
  fieldName: string;
  fieldLabel?: string;
};

export const getEntitiesBasedOnFieldName = (array: FieldNameOption[]): Record<string, FieldNameOption[]> => {
  if (!Array.isArray(array)) {
    return {};
  }

  return array.reduce((acc, item) => {
    if (acc?.[item.fieldName]) {
      acc[item.fieldName] = [...(acc[item.fieldName] || []), item];

      return acc;
    }

    acc[item.fieldName] = [item];

    return acc;
  }, {});
};

export const getTalentUnionsInput = ({ autofillByRoleCriteria, array }: TalentUnionsArgs): string[] => {
  if (!Array.isArray(array)) {
    return [];
  }

  let talentUnionOptions = [];

  if (autofillByRoleCriteria) {
    talentUnionOptions = array;
  } else {
    const isSomeSagAftraChildrenExclusionChecked = array.some((option) =>
      sagAftraChildrenExclusion.includes(option.value as gqlTalentUnionEnum)
    );
    const talentUnionsWithoutSagAftra = array.filter((item) => item.value !== sagAftraOption.value);

    talentUnionOptions = isSomeSagAftraChildrenExclusionChecked
      ? talentUnionsWithoutSagAftra
      : talentUnionsWithoutSagAftra.filter((item) => item.value !== TALENT_UNION.NON_UNION);
  }

  return getEntityIds({
    asNumber: false,
    array: talentUnionOptions,
  }) as string[];
};

export const getExcludeTalentUnionsInput = ({ autofillByRoleCriteria, array }: TalentUnionsArgs): string[] => {
  if (!Array.isArray(array)) {
    return [];
  }

  return getEntityIds({
    asNumber: false,
    array: autofillByRoleCriteria ? array : array.filter((item) => item.value !== sagAftraOption.value),
  }) as string[];
};

export const getTalentTypeInput = (
  talentTypes: SelectOption[]
): Record<string, gqlProfileSearchInput['hasRepresentation']> => {
  if (!Array.isArray(talentTypes)) {
    return {};
  }

  const hasRepped = talentTypes.find((tt) => tt.value === FiltersNameEnum.represented);
  const hasUnrepped = talentTypes.find((tt) => tt.value === FiltersNameEnum.unrepresented);
  const hasBothTypes = hasRepped && hasUnrepped;
  const hasNone = talentTypes?.length === 0;

  // if both options, or no options are selected we return an empty filter
  // which returns both represented and unrepresented results
  if (hasBothTypes || hasNone) {
    return {};
  }

  // if hasRepresentation = true the query will return only represented results
  // if hasRepresentation = false then only non-repped results will get returned
  return { hasRepresentation: Boolean(hasRepped) };
};

export const getIsNewInput = (isNew: boolean): Record<string, gqlProfileSearchInput['isNew']> => {
  return {
    ...(isNew && {
      isNew: true,
    }),
  };
};

export const getAgeRangeInput = ({
  filterName,
  switchTo,
  max,
  min,
}: { filterName: string; switchTo: boolean } & RangeArgs): Record<string, gqlAgeRangeInput> => {
  return {
    ...((max || min) && {
      [filterName]: {
        inMonths: switchTo,
        ageMax: Number(max) || undefined,
        ageMin: Number(min) || undefined,
      },
    }),
  };
};

export const getRangeInput = ({
  asNumber,
  filterName,
  max,
  min,
}: { asNumber?: boolean; filterName: string } & RangeArgs): Record<string, Record<string, gqlIdRangeInput>> => {
  const from = safeGetValue(min);
  const to = safeGetValue(max);

  return {
    ...((from || to) && {
      [filterName]: {
        from: (asNumber ? Number(from) : from) || undefined,
        to: (asNumber ? Number(to) : to) || undefined,
      },
    }),
  };
};

export const getWillingnessInput = ({
  willingToHairColor,
  willingToHairStyle,
  willingToFacialHair,
  otherWilllingnessIds = [],
}): gqlProfileSearchInput['willingnessIds'] => {
  const willingnessIds = [];

  if (willingToHairColor) {
    willingnessIds.push(WillingToEnum.hairColor);
  }

  if (willingToHairStyle) {
    willingnessIds.push(WillingToEnum.hairStyle);
  }

  if (willingToFacialHair) {
    willingnessIds.push(WillingToEnum.facialHair);
  }

  return [...otherWilllingnessIds, ...willingnessIds];
};

export const getTattoosInput = ({ list, note }): Record<string, gqlProfileSearchInput['tattoo']> => {
  return {
    ...((!_isEmpty(list) || note) && {
      tattoo: {
        tattooId: !_isEmpty(list) ? getEntityIds({ array: list }) : undefined,
        tattooNote: note || undefined,
      },
    }),
  };
};

export const getPiercingsInput = ({ list, note }): Record<string, gqlProfileSearchInput['piercing']> => {
  return {
    ...((!_isEmpty(list) || note) && {
      piercing: {
        piercingId: !_isEmpty(list) ? getEntityIds({ array: list }) : undefined,
        piercingNote: note || undefined,
      },
    }),
  };
};

export const getGenderSizeRangeInput = ({
  filterName,
  menSizeRange,
  womenSizeRange,
  childrenSizeRange,
}: {
  filterName: string;
  menSizeRange?: RangeArgs;
  womenSizeRange?: RangeArgs;
  childrenSizeRange?: RangeArgs;
}): Record<string, gqlIdRangeInput> => {
  const from =
    safeGetValue(menSizeRange?.min) || safeGetValue(womenSizeRange?.min) || safeGetValue(childrenSizeRange?.min);
  const to =
    safeGetValue(menSizeRange?.max) || safeGetValue(womenSizeRange?.max) || safeGetValue(childrenSizeRange?.max);

  return {
    ...((from || to) && {
      [filterName]: {
        from: from ? Number(from) : undefined,
        to: to ? Number(to) : undefined,
      },
    }),
  };
};

export const getShoeWidthInput = ({
  menShoeWidth,
  womenShoeWidth,
  childrenShoeWidth,
}): Record<string, gqlProfileSearchInput['shoeModifierId']> => {
  const shoeModifierId = safeGetValue(menShoeWidth) || safeGetValue(womenShoeWidth) || safeGetValue(childrenShoeWidth);

  return {
    ...(shoeModifierId && {
      shoeModifierId,
    }),
  };
};

export const getSizeInput = ({
  boysSizeRange,
  girlsSizeRange,
  toddlersSizeRange,
  infantsSizeRange,
}: {
  boysSizeRange: RangeArgs;
  girlsSizeRange: RangeArgs;
  toddlersSizeRange: RangeArgs;
  infantsSizeRange: RangeArgs;
}): Record<string, gqlProfileSearchInput['sizeId']> => {
  const from =
    safeGetValue(boysSizeRange.min) ||
    safeGetValue(girlsSizeRange.min) ||
    safeGetValue(toddlersSizeRange.min) ||
    safeGetValue(infantsSizeRange.min);
  const to =
    safeGetValue(boysSizeRange.max) ||
    safeGetValue(girlsSizeRange.max) ||
    safeGetValue(toddlersSizeRange.max) ||
    safeGetValue(infantsSizeRange.max);

  return {
    ...((from || to) && {
      sizeId: {
        from: from ? Number(from) : undefined,
        to: to ? Number(to) : undefined,
      },
    }),
  };
};

export const getSkillsInput = (
  skills: { value: string | number }[],
  skillLevel: SelectRadioOption
): gqlProfileSearchInput['skills'] => {
  if (Array.isArray(skillLevel?.value) && skillLevel?.value?.length > 0) {
    return _flatMap(skillLevel.value, (skillLevelId) =>
      skills.map((item) => ({ id: item.value, levelId: skillLevelId }))
    );
  }

  if (!Array.isArray(skills)) {
    return [];
  }

  return skills.map((item) => ({
    id: item.value,
  }));
};

export const getPetInput = ({
  dogBreed,
  otherAnimals,
}: {
  dogBreed: SelectOption[];
  otherAnimals: SelectOption[];
}): Record<string, gqlProfileSearchInput['petIds']> => {
  const petIds = _flatMap([...(dogBreed || []), ...(otherAnimals || [])], (pet) => Number(pet.value));

  return {
    ...(!_isEmpty(petIds) && {
      petIds,
    }),
  };
};

export const getPetSizeInput = (petSize: SelectOption): Record<string, gqlProfileSearchInput['petSizeId']> => {
  return {
    ...(petSize && {
      petSizeId: Number(petSize),
    }),
  };
};

export const getVehicleInput = ({
  type,
  yearRange: yearRangeArgs,
  make,
  model,
}): Record<string, gqlProfileSearchInput['vehicle']> => {
  const { yearRange } = getRangeInput({ asNumber: true, filterName: 'yearRange', ...yearRangeArgs });

  return {
    ...((type?.value || !_isEmpty(yearRange) || make?.value || model) && {
      vehicle: {
        ...(type?.value && {
          typeId: Number(type?.value),
        }),
        ...(!_isEmpty(yearRange) && {
          yearRange,
        }),
        ...(make?.value && {
          makeId: Number(make?.value),
        }),
        ...(model && {
          name: model,
        }),
      },
    }),
  };
};

export const getDateRangeInput = ({
  filterName,
  dateMax,
  dateMin,
}: {
  filterName: string;
  dateMin?: Date | string;
  dateMax?: Date | string;
}): Record<string, Record<string, Date | Moment>> => {
  return {
    ...((dateMax || dateMin) && {
      [filterName]: {
        dateMin: removeTimeZoneFromDate(dateMin) || undefined,
        dateMax: removeTimeZoneFromDate(dateMax) || undefined,
      },
    }),
  };
};

export const getMinMaxValueByName = (filters, name) => {
  return {
    max: safeGetValue(filters[name]?.[FiltersNameEnum.max]),
    min: safeGetValue(filters[name]?.[FiltersNameEnum.min]),
  };
};
